'use client';
import { useState, useRef, useCallback, useEffect } from 'react';
import { IconCloseRound } from '@defign/asset';
import { usePortal } from '@financial/common-hooks';
import clsx from 'clsx';
import { HalfLayerWrapperStyle } from './HalfLayerWrapper.style.mjs';
import Overlay from './Overlay.mjs';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

const HALF_TYPE = {
  ROUND: 'round',
  FILTER: 'type_filter',
  DARK: 'dark-type',
  MESSAGE: 'message-type',
  NOTFULL: 'notfull-type',
  NO_BACKGROUND: 'no-background',
  LIMIT_WIDTH: 'limit-width',
  LIMIT_WIDTH_BIG: 'limit-width-big'
};
const TRANSITION = {
  ENTER: 'enter',
  LEAVE: 'leave'
};
const CENTER_LAYER_BROWSER_WIDTH = 1152;
const getDataAttr = (element, attributeName) => element.dataset[attributeName];
const CloseButton = _ref => {
  let {
    onClose,
    closeCustomBlind
  } = _ref;
  return /*#__PURE__*/jsxs("button", {
    type: "button",
    className: clsx('button-close'),
    onClick: onClose,
    children: [/*#__PURE__*/jsx(IconCloseRound, {
      width: 15,
      height: 15
    }), /*#__PURE__*/jsx("span", {
      className: "blind",
      children: closeCustomBlind ? /*#__PURE__*/jsxs(Fragment, {
        children: [closeCustomBlind, " \uB808\uC774\uC5B4 \uB2EB\uAE30"]
      }) : /*#__PURE__*/jsx(Fragment, {
        children: "\uB808\uC774\uC5B4 \uB2EB\uAE30"
      })
    })]
  });
};

/**
 * @deprecated
 * @description 이 함수는 더이상 유지보수 되지 않습니다. @defign/ui의 useBottomModal 를 사용해주세요.
 */
function HalfLayerWrapper(_ref2) {
  let {
    children = null,
    type = HALF_TYPE.ROUND,
    onClose,
    showCloseButton,
    isShow,
    prerender,
    onDestroyed,
    preventBackgroundScroll = true,
    isOverflowAuto = false,
    outerWrapperClass,
    innerWrapperClass,
    overlayOpacity = 0.5,
    keepPreviousScrollPosition,
    id,
    closeCustomBlind,
    zIndex
  } = _ref2;
  const LayerID = `HalfLayerWrapper_position_${id}`;
  const Portal = usePortal();
  const [isRunning, setIsRunning] = useState(false);
  const [direction, setDirection] = useState(TRANSITION.LEAVE);
  const ref = useRef(null);
  const isCenterLayer = typeof window !== 'undefined' ? window.innerWidth >= CENTER_LAYER_BROWSER_WIDTH : false;
  const halfLayerWrapperClassName = Array.isArray(type) ? clsx('article', ...type) : clsx('article', type);
  const handleHalfLayerWrapperHide = useCallback(() => {
    onDestroyed?.();
    setIsRunning(false);
  }, [onDestroyed]);
  const handleTransitionEnd = e => {
    if (!isCenterLayer && getDataAttr(e.currentTarget, 'direction') === TRANSITION.LEAVE) {
      handleHalfLayerWrapperHide();
    }
  };
  useEffect(() => {
    if (!isShow && isCenterLayer && isRunning && direction === TRANSITION.LEAVE) {
      handleHalfLayerWrapperHide();
    }
  }, [direction, handleHalfLayerWrapperHide, isCenterLayer, isRunning, isShow]);
  useEffect(() => {
    if (isShow) {
      setIsRunning(true);
      requestAnimationFrame(() => setDirection(TRANSITION.ENTER));
    } else {
      requestAnimationFrame(() => setDirection(TRANSITION.LEAVE));
    }
  }, [isShow]);
  useEffect(() => {
    if (isShow) {
      window.requestAnimationFrame(() => {
        if (ref.current && keepPreviousScrollPosition) {
          const sessionStorageValue = sessionStorage.getItem(LayerID);
          if (sessionStorageValue) {
            try {
              ref.current?.scrollTo(0, Number(sessionStorageValue));
            } catch {
              // NOTHING TO DO
            }
          }
        }
      });
    } else {
      if (keepPreviousScrollPosition) {
        if (process.env.NODE_ENV !== 'production' && !id) {
          // eslint-disable-next-line no-console
          console.warn('각 HalfLayerWrapper의 스크롤 포지션을 정확히 유지하기 위해서는 유니크한 id props를 넘겨주어야 합니다.');
        }
        const pos = ref.current?.scrollTop;
        if (pos !== undefined) {
          sessionStorage.setItem(LayerID, `${pos}`);
        }
      }
    }
  }, [isShow, LayerID, keepPreviousScrollPosition, id]);
  return /*#__PURE__*/jsx(Portal, {
    children: (isRunning || prerender) && /*#__PURE__*/jsxs(HalfLayerWrapperStyle, {
      className: clsx(halfLayerWrapperClassName, outerWrapperClass, {
        hide: prerender && !isShow && !isCenterLayer,
        'hide-no-animation': prerender && !isShow && isCenterLayer
      }),
      zIndex: zIndex,
      children: [/*#__PURE__*/jsx("div", {
        className: clsx('halflayer-wrapper'),
        children: /*#__PURE__*/jsxs("div", {
          ref: ref,
          className: clsx('halflayer-inner', innerWrapperClass, {
            scroll: isOverflowAuto,
            [direction]: !isCenterLayer
          }),
          "data-direction": direction,
          onTransitionEnd: handleTransitionEnd,
          children: [children, showCloseButton && /*#__PURE__*/jsx(CloseButton, {
            onClose: onClose,
            closeCustomBlind: closeCustomBlind
          })]
        })
      }), /*#__PURE__*/jsx(Overlay, {
        isShow: isShow,
        onClick: onClose,
        preventBackgroundScroll: preventBackgroundScroll,
        opacity: overlayOpacity,
        duration: isCenterLayer ? 0 : 200
      })]
    })
  });
}

export { HALF_TYPE, HalfLayerWrapper as default };
